import { Injectable } from '@angular/core';
import { HttpRequest, HttpResponse, HttpHandler, HttpEvent, HttpClient, HttpInterceptor } from '@angular/common/http';
import { Observable, Subject, from } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

import * as CryptoUtils from '../utils/crypto.utils';
import { CacheService, CacheTarget } from '../state/cache/cache.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class RequestGroupingInterceptor implements HttpInterceptor {
	private readonly targets: CacheTarget[] = [
		{ method: 'POST', routeFragments: [environment.apiUrl, '/data-source/'] }
	];

	private requests = new Map<string, {subject: Subject<any>, subscribers: number}>();
	private delayWindow = 500; // Time in milliseconds to wait for additional requests

	constructor(
		private readonly http: HttpClient,
		private readonly cacheService: CacheService
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const [canGroup, duration] = this.cacheService.canCache(req.method, req.url, this.targets);
		if (canGroup && !environment.disableCacheInterceptor) {
			return from(this.manageRequest(req, next));
		}

		return next.handle(req);
	}

	/**
	 * Manages the request and returns an Observable that will share the same result with all subscribers
	 * Evaluates to determine if the request is new or if it should be combined with an existing request
	 */
	async manageRequest(req: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
		console.log('Attempting to group request for', req.url);

		let key = `${req.method}-${encodeURI(req.urlWithParams)}`;
		key += await this.generateKey(req).catch(() => '');

		// console.log('Request key:', key, graphs, req.body);

		// If the request is new, create a new Subject and send the request
		if (!this.requests.has(key)) {
			// console.log('New request, creating a new Subject');
			const subject = new Subject<HttpEvent<any>>();
			this.requests.set(key, { subject, subscribers: 1 });

			// Wait for a short period of time to see if another request with the same key is made
			setTimeout(() => {
				if (this.requests.has(key)) {
					const combinedReq = this.requests.get(key);
					// console.log('Sending request', combinedReq);
					const isFormData = req.body instanceof FormData;
					const reqBody = isFormData ? req.body : (typeof req.body === 'object' && req.body !== null ? {...req.body} : {});

					this.sendRequest(
						req.clone({ body: reqBody }),
						next
					).subscribe(
						response => combinedReq.subject.next(response),
						error => combinedReq.subject.error(error),
						() => combinedReq.subject.complete()
					);
					this.requests.delete(key);
				}
			}, this.delayWindow);

		} else {
			// Same request has been made, increase the number of subscribers
			console.log('Existing request, updating the number of subscribers.');
			const existingReq = this.requests.get(key);
			existingReq.subscribers++;
		}

		// Return an Observable that will share the same result with all subscribers
		return this.requests.get(key).subject.asObservable().pipe(
			shareReplay(1)
		).toPromise();
	}

	/**
	 * Sends the request and returns an Observable that will share the same result with all subscribers.
	 */
	private sendRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Directly handle the request here if you need to customize the sending logic,
		// or just pass it to the next handler.
		return next.handle(req);
	}

	/**
	 * Generates a unique key for the request based on the request body
	 */
	private async generateKey(req: HttpRequest<any>) {
		if (req.body instanceof FormData) {
			return 'file-upload-' + Date.now();
		}

		// Remove anything that we don't want to count towards the compare hash.
		const body = {
			...req.body
		};

		// Save any parameters that we want to include in the compare hash.

		// console.log('Hashing request body:', body);

		return await CryptoUtils.hashObject(body);
	}
}
